import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { UserData } from '../../data-models/user-models/user-data.model';
import { generalValidator } from '../../data-models/validator.model';
import { AuthenticationService } from '../../services/authentication.service';
import { Tel, TelInputComponent } from '../../tel-input/tel-input.component';

@Component({
    selector: 'app-contact-info-dialog',
    templateUrl: './contact-info-dialog.component.html',
    styleUrls: ['./contact-info-dialog.component.css'],
    standalone: false
})

export class ContactInfoDialogComponent implements OnInit {

  @ViewChild('telInput') telInput!: TelInputComponent; // telephone input

  // get the current user details
  userDetails: UserData = new UserData();

  // show error message when field is invalid
  emailError: string = 'Required';
  postalError: string = 'Required';

  // phone validation
  userPhone: Tel = new Tel();
  phoneValid: boolean = false;

  // regular expression for Canadian postal code validation
  // from https://stackoverflow.com/questions/15774555/efficient-regex-for-canadian-postal-code-function
  postalRegex: RegExp = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

  profileForm: UntypedFormGroup = new UntypedFormGroup({
    address: new FormControl<string>('', [Validators.required]),
    city: new FormControl<string>('', [Validators.required]),
    postalCode: new FormControl<string>('', [Validators.required, Validators.pattern(this.postalRegex)]),
    phoneNum: new FormControl<Tel>(new Tel(), [Validators.required, generalValidator()]),
  });

  constructor(
    private dialogRef: MatDialogRef<ContactInfoDialogComponent>,
    private authenticationService: AuthenticationService,
    public sharedMethodsService: SharedMethodsService
  ) {

    this.authenticationService.getUserDetails().then(() => {
      this.userDetails = UserStorageService.currUser;
      this.initialize();
    });

  }


  /*  ---------- Initialization Methods  ---------- */


  ngOnInit(): void {

   this.validatePostalCode();
   this.validateEmail();

  }

  initialize(): void {

    this.profileForm.patchValue({
      address: this.userDetails.address,
      city: this.userDetails.city,
      postalCode: this.userDetails.postalCode,
      phoneNum: this.userDetails.phoneNum
    });

  }


  /*  ---------- Validation Methods  ---------- */


  // consider moving to validation service:
  validateEmail(): void {

    let userEmail: AbstractControl<any, any> | null = this.profileForm.get('userEmail');

    // contact email validation
    userEmail?.valueChanges.subscribe(() => {
      let userEmailErrors: ValidationErrors | null | undefined = userEmail?.errors;
      if (userEmailErrors?.required) {
        this.emailError = 'Required';
      }
      else if (userEmailErrors?.email) {
        this.emailError = 'Invalid format';
      }
      else {
        this.emailError = '';
      }
    });

  }

  // consider moving to validation service:
  validatePostalCode(): void {

    let postalCode: AbstractControl<any, any> | null = this.profileForm.get('postalCode');

    // postal code validation
    postalCode?.valueChanges.subscribe(() => {
      let postalCodeErrors: ValidationErrors | null | undefined = postalCode?.errors;
      if (postalCodeErrors?.required) {
        this.postalError = 'Required';
      }
      else if (postalCodeErrors?.pattern) {
        this.postalError = 'Invalid format';
      }
      else {
        this.postalError = '';
      }
    });

  }

  // validate the updated phone number
  validatePhoneNum(): void {

    this.telInput.touched = true;
    this.telInput.stateChanges.next();
    this.profileForm.get('phoneNum')?.updateValueAndValidity();
    this.userPhone = Tel.transformAnonymous(this.profileForm.get('phoneNum')?.value);
    this.phoneValid = this.userPhone.isValid();

  }


  /*  ---------- Update Methods ---------- */


  updateContactInfo(update: boolean): void {

    if (update) {
      this.attemptUpdate();
    }
    else {
      this.dialogRef.close(false);
    }   

  }

  attemptUpdate(): void {

    // verify phone number
    this.validatePhoneNum();

    // check profile field entry validity
    if (this.profileForm.invalid || !this.phoneValid) {
      this.sharedMethodsService.toastMissingInformation();
      return;
    }

    // get and save user details
    this.userDetails.address = this.profileForm.get('address')?.value;
    this.userDetails.city = this.profileForm.get('city')?.value;
    this.userDetails.postalCode = this.profileForm.get('postalCode')?.value;
    this.userDetails.phoneNum = this.userPhone;
    UserStorageService.saveUser(this.userDetails).then(() => {
      this.dialogRef.close(true);
    });

  }

}
